<template>
  <section id="calculadora-familiar" class="family-calc-section">
    <div class="container">
      <div class="calc-grid">
        <div class="copy">
          <h2 class="section-title">¿Cuánto pagaría tu familia?</h2>
          <p class="section-subtitle">
            Agrega a las personas de tu hogar y mira el precio mensual. Niños desde 2 años.
          </p>
          <button type="button" class="btn btn-calc-cta" @click="goBuyPlan">
            Quiero este precio <i class="fas fa-chevron-right ml-2"></i>
          </button>
        </div>

        <div class="builder" aria-live="polite">
          <h3>Calculadora familiar</h3>
          <p class="hint">Ajusta adultos y niños.</p>

          <div class="stepper">
            <span>Adultos</span>
            <div class="ctl">
              <button type="button" :disabled="adults <= 1" aria-label="Quitar adulto" @click="stepAdults(-1)">
                −
              </button>
              <output>{{ adults }}</output>
              <button type="button" :disabled="adults >= 4 || atFamilyMax" aria-label="Agregar adulto" @click="stepAdults(1)">
                +
              </button>
            </div>
          </div>

          <div class="stepper">
            <span>Niños (desde 2 años)</span>
            <div class="ctl">
              <button type="button" :disabled="kids <= 0" aria-label="Quitar niño" @click="stepKids(-1)">
                −
              </button>
              <output>{{ kids }}</output>
              <button type="button" :disabled="kids >= 5 || atFamilyMax" aria-label="Agregar niño" @click="stepKids(1)">
                +
              </button>
            </div>
          </div>

          <div class="quote">
            <div>
              <div class="plan">{{ planLabel }}</div>
              <div class="plan">{{ perLabel }}</div>
            </div>
            <div class="price">
              ${{ monthlyPrice }}<small>/mes</small>
            </div>
          </div>
          <div class="save">{{ saveLabel }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { famPrice, MARKET_BY_SIZE } from '../data/hmpPlans2027'
import { FAMILY_MAX_PEOPLE } from '@/core/utils/membershipFamily'
import plansService from '@/core/services/api/landings/plans'
import { familyTierAmount, pickBillingPrice, planBillingPrices } from '@/core/utils/membershipBilling'

/**
 * Calculadora familiar (mockup hero builder).
 */
export default {
  name: 'FamilyQuoteCalculator',
  data() {
    return {
      adults: 1,
      kids: 0,
      apiPrices: []
    }
  },
  computed: {
    totalPeople() {
      return this.adults + this.kids
    },
    atFamilyMax() {
      return this.totalPeople >= FAMILY_MAX_PEOPLE
    },
    monthlyPrice() {
      if (this.totalPeople === 1) return 55
      const offer = pickBillingPrice(this.apiPrices, 'month')
      if (offer) return familyTierAmount(offer, this.totalPeople)
      return famPrice(this.totalPeople)
    },
    planLabel() {
      if (this.totalPeople === 1) return 'Plan Tu Salud'
      return `Plan Familiar · ${this.totalPeople} personas`
    },
    perLabel() {
      if (this.totalPeople === 1) return 'Consultas ilimitadas a $0'
      const per = (this.monthlyPrice / this.totalPeople).toFixed(2).replace(/\.00$/, '')
      return `$${per} por persona`
    },
    saveLabel() {
      const m = MARKET_BY_SIZE[this.totalPeople]
      if (!m || m <= this.monthlyPrice) return ''
      const year = Math.round((m - this.monthlyPrice) * 12)
      return `Ahorras $${year.toLocaleString('en-US')} al año frente a otro plan del mercado`
    }
  },
  mounted() {
    this.loadFamilyPrices()
  },
  methods: {
    async loadFamilyPrices() {
      try {
        const response = await plansService.getAllPlans()
        const list = response && Array.isArray(response.data) ? response.data : []
        const plan = list.find((item) => {
          const name = String(item.name || '').toLowerCase()
          return item.plan_kind === 'family' || name.includes('familiar') || name.includes('familia')
        })
        this.apiPrices = planBillingPrices(plan)
      } catch (error) {
        this.apiPrices = []
      }
    },
    stepAdults(delta) {
      if (delta > 0 && this.atFamilyMax) return
      this.adults = Math.min(4, Math.max(1, this.adults + delta))
    },
    stepKids(delta) {
      if (delta > 0 && this.atFamilyMax) return
      this.kids = Math.min(5, Math.max(0, this.kids + delta))
    },
    /** Abre la compra del plan calculado (Tu Salud o Familiar), no contacto. */
    goBuyPlan() {
      this.$emit('want-this-price', {
        totalPeople: this.totalPeople,
        adults: this.adults,
        kids: this.kids,
        monthlyPrice: this.monthlyPrice
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.family-calc-section {
  padding: 72px 0;
  background: $landing-primary-color;
  color: #ffffff;
}

.calc-grid {
  display: grid;
  grid-template-columns: 1fr minmax(280px, 380px);
  gap: 40px;
  align-items: center;
}

.section-title {
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 800;
  color: #ffffff !important;
  margin: 0 0 0.75rem;
}

.section-subtitle {
  color: #b9cad6;
  font-size: 1.1rem;
  margin: 0 0 1.5rem;
  max-width: 40ch;
}

.btn-calc-cta {
  display: inline-flex;
  align-items: center;
  border: none;
  cursor: pointer;
  background: $landing-secondary-color;
  color: $landing-primary-color;
  font-weight: 700;
  border-radius: 10px;
  padding: 12px 20px;
  text-decoration: none;

  i {
    color: $landing-primary-color !important;
  }

  &:hover {
    background: $landing-blue-light;
    color: $landing-primary-color;
    text-decoration: none;

    i {
      color: $landing-primary-color !important;
    }
  }
}

.builder {
  background: #fff;
  color: $landing-text-dark;
  border-radius: 22px;
  padding: 24px;
  box-shadow: 0 24px 60px rgba(4, 20, 36, 0.35);
  border: 1px solid transparent;
}

.builder h3 {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 4px;
  color: $landing-primary-color;
}

.hint {
  font-size: 0.9rem;
  color: $landing-text-medium;
  margin-bottom: 16px;
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-top: 1px solid $landing-border-light;
  font-weight: 600;
}

.ctl {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ctl button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid $landing-border-light;
  background: $landing-bg-light;
  color: $landing-text-dark;
  font-size: 1.25rem;
  line-height: 1;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.ctl output {
  min-width: 22px;
  text-align: center;
  font-weight: 700;
  font-size: 1.1rem;
}

.quote {
  margin-top: 14px;
  padding-top: 16px;
  border-top: 2px solid $landing-primary-color;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.plan {
  font-size: 0.9rem;
  color: $landing-text-medium;
}

.price {
  font-weight: 850;
  font-size: 2.75rem;
  line-height: 1;
  color: $landing-primary-color;

  small {
    font-size: 1rem;
    font-weight: 600;
    color: $landing-text-medium;
  }
}

.save {
  margin-top: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: $landing-teal;
  min-height: 20px;
}

@media (max-width: 860px) {
  .family-calc-section {
    padding: 48px 16px;
  }

  .calc-grid {
    grid-template-columns: 1fr;
  }

  .price {
    font-size: 2.25rem;
  }
}
</style>
